// src/components/panels/SalesPanel.js
import React, { useEffect, useRef } from 'react';

const SalesPanel = ({
    targetMarginInput,
    setTargetMarginInput,
    handleSetTargetMargin,
    bulkRPDiscount,
    setBulkRPDiscount,
    handleBulkUpdateRPDiscount,
    bulkCPDiscount,
    setBulkCPDiscount,
    handleBulkUpdateCPDiscount,
    bulkQuantity,
    setBulkQuantity,
    handleBulkUpdateQuantity,
    headerData,
    handleHeaderChange,
    totals,
    formatCurrencyDynamic,
    areButtonsDisabled
}) => {
    // CP Subtotal pro výpočty
    const cpSubtotal = totals.totalCPEur || 0;
    
    // Ref pro sledování, zda jsme už dopočetli procento při načtení
    const initialSyncDone = useRef(false);

    // Při načtení z CRM: pokud je DiscountAbs > 0 a DiscountRel je 0/chybí, dopočítat procento
    useEffect(() => {
        if (initialSyncDone.current) return;
        if (!headerData || cpSubtotal <= 0) return;
        
        const discountAbs = parseFloat(headerData.DiscountAbs) || 0;
        const discountRel = parseFloat(headerData.DiscountRel) || 0;
        
        // Pokud máme absolutní slevu ale ne procento, dopočítat
        if (discountAbs > 0 && discountRel === 0) {
            const calculatedRel = (discountAbs / cpSubtotal * 100);
            handleHeaderChange({ target: { name: 'DiscountRel', value: Math.round(calculatedRel * 1000) / 1000 } });
            initialSyncDone.current = true;
        } else if (discountAbs > 0 || discountRel > 0) {
            // Máme obě hodnoty nebo jen procento - považujeme za hotové
            initialSyncDone.current = true;
        }
    }, [headerData, cpSubtotal, handleHeaderChange]);

    // Handler pro změnu procentuální slevy - dopočítá absolutní
    const handleDiscountRelChange = (e) => {
        const relValue = parseFloat(e.target.value) || 0;
        const absValue = cpSubtotal > 0 ? (cpSubtotal * relValue / 100) : 0;
        // Aktualizovat obě hodnoty
        handleHeaderChange({ target: { name: 'DiscountRel', value: relValue } });
        handleHeaderChange({ target: { name: 'DiscountAbs', value: Math.round(absValue * 100) / 100 } });
    };

    // Handler pro změnu absolutní slevy - dopočítá procento
    const handleDiscountAbsChange = (e) => {
        const absValue = parseFloat(e.target.value) || 0;
        const relValue = cpSubtotal > 0 ? (absValue / cpSubtotal * 100) : 0;
        // Aktualizovat obě hodnoty
        handleHeaderChange({ target: { name: 'DiscountAbs', value: absValue } });
        handleHeaderChange({ target: { name: 'DiscountRel', value: Math.round(relValue * 1000) / 1000 } });
    };

    return (
        <>
            <div className="panel tools-panel">
                <h3 className="panel-title">Sales Tools & Bulk Edits</h3>
                <div className="panel-content tool-section">
                    <div className="tool-group"><strong>Set Margin %:</strong><input type="number" value={targetMarginInput} onChange={(e) => setTargetMarginInput(e.target.value)} placeholder="e.g. 15" /><button onClick={handleSetTargetMargin} disabled={areButtonsDisabled} className="btn-info">Set</button></div>
                    <div className="tool-group"><strong>RP Disc. %:</strong><input type="number" value={bulkRPDiscount} onChange={(e) => setBulkRPDiscount(e.target.value)} placeholder="New discount" /><button onClick={handleBulkUpdateRPDiscount} disabled={areButtonsDisabled} className="btn-secondary">Set</button></div>
                    <div className="tool-group"><strong>CP Disc. %:</strong><input type="number" value={bulkCPDiscount} onChange={(e) => setBulkCPDiscount(e.target.value)} placeholder="New discount" /><button onClick={handleBulkUpdateCPDiscount} disabled={areButtonsDisabled} className="btn-secondary">Set</button></div>
                    <hr className="tool-separator" />
                    <div className="tool-group"><strong>Quantity:</strong><input type="number" value={bulkQuantity} onChange={(e) => setBulkQuantity(e.target.value)} placeholder="New quantity" /><button onClick={handleBulkUpdateQuantity} disabled={areButtonsDisabled} className="btn-secondary">Set</button></div>
                </div>
            </div>
            <div className="panel sums-panel">
                <h3 className="panel-title">Sums</h3>
                <div className="panel-content">
                    <div className="currency-settings">
                        <div className="tool-group currency-group"><strong>Currency:</strong><select name="Invoicing_Currency" value={headerData.Invoicing_Currency || 'EUR'} onChange={handleHeaderChange}><option value="EUR">EUR</option><option value="CZK">CZK</option><option value="USD">USD</option></select></div>
                        <div className="tool-group currency-group"><strong>Rate:</strong><input type="number" name="Fixed_Exchange_Rate" value={headerData.Fixed_Exchange_Rate || 1.0} onChange={handleHeaderChange} step="0.01" /></div>
                    </div>
                    <table className="totals-table">
                        <thead><tr><th>Description</th><th>EUR</th>{headerData.Invoicing_Currency !== 'EUR' && (<th>{headerData.Invoicing_Currency}</th>)}</tr></thead>
                        <tbody>
                            {/* QTY Total */}
                            <tr>
                                <td>QTY Total</td>
                                <td style={{ textAlign: 'right' }}>{totals.totalQuantity} pcs</td>
                                {headerData.Invoicing_Currency !== 'EUR' && <td></td>}
                            </tr>
                            {/* OP Total */}
                            <tr>
                                <td>OP Total</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrencyDynamic(totals.totalFinalOPEur, 'EUR', 1)}</td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right' }}>{formatCurrencyDynamic(totals.totalFinalOPEur, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}</td>
                                )}
                            </tr>
                            {/* CP Subtotal (před slevou) */}
                            <tr>
                                <td>CP Subtotal</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrencyDynamic(totals.totalCPEur, 'EUR', 1)}</td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right' }}>{formatCurrencyDynamic(totals.totalCPEur, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}</td>
                                )}
                            </tr>
                            {/* Additional Discount - obě pole vedle sebe */}
                            <tr className="input-row">
                                <td>
                                    <span>Discount</span>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <div className="discount-dual-input">
                                        <input 
                                            type="number" 
                                            name="DiscountRel" 
                                            value={headerData.DiscountRel || ''} 
                                            onChange={handleDiscountRelChange} 
                                            placeholder="0"
                                            step="0.1"
                                            min="0"
                                            max="100"
                                            className="inline-input"
                                            style={{ width: '60px' }}
                                        />
                                        <span className="input-suffix">%</span>
                                        <span className="input-separator">=</span>
                                        <input 
                                            type="number" 
                                            name="DiscountAbs" 
                                            value={headerData.DiscountAbs || ''} 
                                            onChange={handleDiscountAbsChange} 
                                            placeholder="0"
                                            step="1"
                                            min="0"
                                            className="inline-input"
                                            style={{ width: '80px' }}
                                        />
                                        <span className="input-suffix">€</span>
                                    </div>
                                </td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right', color: '#d9534f' }}>
                                        {totals.discountAmountEur > 0 ? `- ${formatCurrencyDynamic(totals.discountAmountEur, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}` : ''}
                                    </td>
                                )}
                            </tr>
                            {/* CP Total (po slevě) */}
                            <tr style={{ fontWeight: 'bold' }}>
                                <td style={{ fontWeight: 'bold' }}>CP Total</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrencyDynamic(totals.cpTotalAfterDiscount, 'EUR', 1)}</td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrencyDynamic(totals.cpTotalAfterDiscount, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}</td>
                                )}
                            </tr>
                            {/* Margin Abs */}
                            <tr>
                                <td style={{ fontWeight: 'bold' }}>Margin total (Abs)</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: totals.totalMarginAbsEur < 0 ? '#d9534f' : 'inherit' }}>
                                    {formatCurrencyDynamic(totals.totalMarginAbsEur, 'EUR', 1)}
                                </td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: totals.totalMarginAbsEur < 0 ? '#d9534f' : 'inherit' }}>
                                        {formatCurrencyDynamic(totals.totalMarginAbsEur, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}
                                    </td>
                                )}
                            </tr>
                            {/* Margin % */}
                            <tr>
                                <td style={{ fontWeight: 'bold' }}>Margin total (%)</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: totals.totalMarginRel < 0 ? '#d9534f' : 'inherit' }}>
                                    {totals.totalMarginRel.toFixed(2)} %
                                </td>
                                {headerData.Invoicing_Currency !== 'EUR' && <td></td>}
                            </tr>
                            {/* Shipping - input řádek */}
                            <tr className="input-row shipping-row">
                                <td>Shipping</td>
                                <td style={{ textAlign: 'right' }}>
                                    <input 
                                        type="number" 
                                        name="Shipping" 
                                        value={headerData.Shipping || ''} 
                                        onChange={handleHeaderChange} 
                                        placeholder="0"
                                        step="1"
                                        className="inline-input"
                                        style={{ width: '90px' }}
                                    />
                                    <span className="input-suffix"> €</span>
                                </td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right' }}>
                                        {headerData.Shipping ? formatCurrencyDynamic(parseFloat(headerData.Shipping) || 0, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate) : ''}
                                    </td>
                                )}
                            </tr>
                            {/* Grand Total */}
                            <tr className="grand-total-row">
                                <td style={{ fontWeight: 'bold', fontSize: '1.1em' }}>Grand Total</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.1em' }}>
                                    {formatCurrencyDynamic(totals.grandTotal, 'EUR', 1)}
                                </td>
                                {headerData.Invoicing_Currency !== 'EUR' && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.1em' }}>
                                        {formatCurrencyDynamic(totals.grandTotal, headerData.Invoicing_Currency, headerData.Fixed_Exchange_Rate)}
                                    </td>
                                )}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default SalesPanel;